import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const ManufacturingProcessSummary = () => {
  const navigate = useNavigate();

  const steps = [
    {
      title: "Material Selection",
      description: "Premium materials chosen for superior durability, comfort, and elite-level performance.",
      image: "/Boxing/Categories/6.webp",
    },
    {
      title: "Cutting & Stitching",
      description: "Precision cutting and reinforced stitching engineered for strength and flawless finish.",
      image: "/Boxing/Categories/7.webp",
    },
    {
      title: "Quality Inspection",
      description: "Every product undergoes rigorous inspection to ensure premium quality and consistency.",
      image: "/Boxing/Categories/14.webp",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-[#f5f5f5] py-16 md:py-24">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,0,0,0.06),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(0,0,0,0.05),_transparent_32%)]" />
        <div className="absolute top-10 right-[-100px] h-72 w-72 rounded-full bg-[#FF0000]/10 blur-3xl" />
        <div className="absolute bottom-0 left-[-80px] h-80 w-80 rounded-full bg-black/10 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="mx-auto mb-12 max-w-3xl text-center"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/80 px-4 py-2 text-xs sm:text-sm font-medium text-black shadow-lg backdrop-blur-xl">
            <Sparkles size={14} className="text-[#FF0000]" />
            Precision Crafted Excellence
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-black sm:text-4xl md:text-5xl">
            <span className="text-[#FF0000]">Our</span> Manufacturing Process
          </h2>

          <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base md:text-lg">
            From premium material sourcing to final inspection, every step is
            executed with precision to deliver exceptional quality and
            performance.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 35 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.65,
                delay: index * 0.12,
                ease: [0.22, 1, 0.36, 1],
              }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
              className="group relative overflow-hidden rounded-3xl border border-black/10 bg-white/80 shadow-xl backdrop-blur-xl transition-all duration-300"
            >
              {/* Image */}
              <div className="relative overflow-hidden">
                <img
                  src={step.image}
                  alt={step.title}
                  className="h-64 w-full object-cover transition duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />

                {/* Step Number */}
                <div className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-sm font-bold text-black shadow-lg backdrop-blur-xl">
                  0{index + 1}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="mb-2 text-xl font-semibold text-black">
                  {step.title}
                </h3>
                <p className="text-sm leading-relaxed text-gray-600 sm:text-[15px]">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-10 text-center md:mt-12"
        >
          <button
            onClick={() => navigate("/manufacturing-process")}
            className="group inline-flex items-center gap-2 rounded-2xl bg-black px-7 py-4 text-sm font-semibold text-white shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:bg-[#111] sm:text-base"
          >
            Learn More
            <ArrowRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default ManufacturingProcessSummary;